import React,{useState,useEffect,useCallback,useMemo} from 'react';
import {paymentAPI,branchAPI,currencyAPI} from '../utils/api';
import {PageHeader,DataTable,Badge,Modal,fmt,fmtDate} from '../components/common';
import {useAuth} from '../context/AuthContext';
import {DollarSign,TrendingUp,TrendingDown,RefreshCw,Download,X,
  CreditCard,Building2,Search,Eye,CheckCircle,Clock,AlertCircle,XCircle,Lock} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_META={
  completed:{label:'Completed',variant:'success',icon:CheckCircle,color:'var(--success)'},
  pending:  {label:'Pending',  variant:'warning',icon:Clock,       color:'var(--warning)'},
  failed:   {label:'Failed',   variant:'danger',  icon:XCircle,     color:'var(--danger)'},
  refunded: {label:'Refunded', variant:'accent',  icon:AlertCircle, color:'var(--accent)'},
};
const TYPE_META={
  payment:{label:'Payment',variant:'success'},
  refund: {label:'Refund', variant:'danger'},
};

function StatCard({label,value,sub,color,icon:Icon,onClick,active}){
  return(
    <div onClick={onClick}
      style={{background:'var(--surface)',border:`1.5px solid ${active?color:'var(--border)'}`,borderRadius:12,
        padding:'14px 16px',display:'flex',alignItems:'center',gap:12,cursor:onClick?'pointer':'default',
        transition:'all .15s',boxShadow:active?`0 0 0 3px ${color}22`:'none'}}>
      <div style={{width:38,height:38,borderRadius:10,background:color+'22',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
        <Icon size={17} color={color}/>
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:10,color:'var(--td)',fontWeight:700,letterSpacing:.5,textTransform:'uppercase',marginBottom:2}}>{label}</div>
        <div style={{fontSize:17,fontWeight:900,lineHeight:1}}>{value}</div>
        {sub&&<div style={{fontSize:11,color:'var(--td)',marginTop:2}}>{sub}</div>}
      </div>
    </div>
  );
}

function BranchCard({branch,payments,onFilter,active}){
  const total=payments.filter(p=>p.status==='completed'&&p.type==='payment').reduce((s,p)=>s+parseFloat(p.amount||0),0);
  const pending=payments.filter(p=>p.status==='pending').length;
  const refunds=payments.filter(p=>p.type==='refund').length;
  const methods={};
  payments.forEach(p=>{const m=p.method?.name||'Other';methods[m]=(methods[m]||0)+1;});
  return(
    <div onClick={()=>onFilter(branch.id)}
      style={{background:'var(--surface)',border:`2px solid ${active?'var(--primary)':'var(--border)'}`,borderRadius:14,
        padding:16,cursor:'pointer',transition:'all .15s',boxShadow:active?'0 0 0 3px var(--pm)':'none'}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:12}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:34,height:34,borderRadius:9,background:active?'var(--primary)':'var(--s2)',display:'flex',alignItems:'center',justifyContent:'center',transition:'background .15s'}}>
            <Building2 size={15} color={active?'#fff':'var(--tm)'}/>
          </div>
          <div>
            <div style={{fontWeight:700,fontSize:13}}>{branch.name}</div>
            <div style={{fontSize:10,color:'var(--td)'}}>{branch.code||''}</div>
          </div>
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:16,fontWeight:900,color:'var(--success)'}}>{fmt(total)}</div>
          <div style={{fontSize:10,color:'var(--td)'}}>{payments.length} payments</div>
        </div>
      </div>
      <div style={{display:'flex',gap:5,flexWrap:'wrap',marginBottom:10}}>
        <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:20,background:'var(--sm)',color:'var(--success)'}}>
          <CheckCircle size={8} style={{marginRight:3,verticalAlign:'middle'}}/>{payments.filter(p=>p.status==='completed').length} done
        </span>
        {pending>0&&<span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:20,background:'var(--wm)',color:'var(--warning)'}}>
          <Clock size={8} style={{marginRight:3,verticalAlign:'middle'}}/>{pending} pending
        </span>}
        {refunds>0&&<span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:20,background:'var(--am)',color:'var(--accent)'}}>{refunds} refund{refunds!==1?'s':''}</span>}
      </div>
      {Object.keys(methods).length>0&&(
        <div>
          <div style={{fontSize:10,color:'var(--td)',marginBottom:4,fontWeight:600}}>BY METHOD</div>
          <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
            {Object.entries(methods).map(([m,c])=>(
              <span key={m} style={{fontSize:10,padding:'2px 6px',borderRadius:5,background:'var(--pm)',color:'var(--primary)',fontWeight:600}}>{m} x{c}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentDetailModal({payment,onClose}){
  const[panelKhrRate,setPanelKhrRate]=useState(4100);
  useEffect(()=>{currencyAPI.getRates().then(r=>{const rt=(r.data?.data||r.data||[]).find(x=>x.from_currency==='USD'&&x.to_currency==='KHR');if(rt)setPanelKhrRate(parseFloat(rt.rate)||4100);}).catch(()=>{});},[]);
  const fmtKHR=n=>`≈ ៛${Math.round((parseFloat(n)||0)*panelKhrRate).toLocaleString()}`;
  if(!payment)return null;
  const sm=STATUS_META[payment.status]||STATUS_META.completed;
  const Icon=sm.icon;
  return(
    <Modal open onClose={onClose} title={`Payment — ${payment.payment_number||'#'+payment.id}`} width={520}>
      <div style={{padding:'12px 14px',borderRadius:10,marginBottom:18,background:sm.color+'18',border:`1px solid ${sm.color}44`,display:'flex',alignItems:'center',gap:10}}>
        <Icon size={20} color={sm.color}/>
        <div><div style={{fontWeight:700,color:sm.color,fontSize:13}}>{sm.label}</div><div style={{fontSize:11,color:'var(--td)'}}>{payment.type==='refund'?'Refund transaction':'Payment transaction'}</div></div>
        <div style={{marginLeft:'auto',textAlign:'right'}}>
          <div>
            <div style={{fontSize:22,fontWeight:900,color:payment.type==='refund'?'var(--danger)':'var(--success)'}}>{payment.type==='refund'?'-':''}{fmt(payment.amount)}</div>
            {payment.original_currency==='KHR'&&payment.original_amount?(
              <div style={{fontSize:13,color:'var(--warning)',fontWeight:700,marginTop:2}}>
                ៛{Math.round(payment.original_amount).toLocaleString()} KHR
                <span style={{fontSize:11,color:'var(--td)',fontWeight:400}}> (paid in KHR)</span>
              </div>
            ):(
              <div style={{fontSize:11,color:'var(--td)',marginTop:2}}>{fmtKHR(payment.amount)}</div>
            )}
          </div>
        </div>
      </div>
      {[
        ['Payment #',payment.payment_number||`PAY-${payment.id}`],
        ['Date',fmtDate(payment.payment_date)],
        ['Customer',payment.customer?.name||'—'],
        ['Branch',payment.branch?.name||'—'],
        ['Method',payment.method?.name||'—'],
        ['Type',(TYPE_META[payment.type]||TYPE_META.payment).label],
        ['Status',sm.label],
        ['Reference',payment.reference||'—'],
        ['Currency',payment.currency||'USD'],
        ['Notes',payment.notes||'—'],
      ].map(([k,v])=>(
        <div key={k} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 0',borderBottom:'1px solid var(--border)'}}>
          <span style={{fontSize:12,color:'var(--td)',fontWeight:600,minWidth:110}}>{k}</span>
          <span style={{fontSize:13,fontWeight:600}}>{v}</span>
        </div>
      ))}
      {payment.invoice_id&&(
        <div style={{marginTop:14,padding:'12px 14px',background:'var(--pm)',borderRadius:10}}>
          <div style={{fontSize:11,color:'var(--td)',fontWeight:600,marginBottom:2}}>LINKED INVOICE</div>
          <div style={{fontWeight:700,color:'var(--primary)',fontSize:13}}>Invoice ID: #{payment.invoice_id}</div>
        </div>
      )}
      <div className="modal-footer"><button className="btn btn-ghost" onClick={onClose}>Close</button></div>
    </Modal>
  );
}

export default function PaymentsPage(){
  const{isOwner,myBranchId,myBranch}=useAuth();

  const[data,setData]=useState([]);
  const[khrRate,setKhrRate]=useState(4100);
  const fmtKHR=n=>`≈ ៛${Math.round(parseFloat(n||0)*khrRate).toLocaleString()}`;
  const[branches,setBranches]=useState([]);
  const[ld,setLd]=useState(true);
  const[search,setSearch]=useState('');
  // Non-owners are locked to their branch; owners can filter freely
  const[branchF,setBranchF]=useState(myBranchId?String(myBranchId):'');
  const[statusF,setStatusF]=useState('All');
  const[typeF,setTypeF]=useState('All');
  const[methodF,setMethodF]=useState('All');
  const[dateFrom,setDateFrom]=useState('');
  const[dateTo,setDateTo]=useState('');
  const[tab,setTab]=useState('list');
  const[detail,setDetail]=useState(null);

  // Only owners load branch list for the selector
  useEffect(()=>{
    if(isOwner) branchAPI.getAll({limit:100}).then(r=>setBranches(r.data.data||[])).catch(()=>{});
    currencyAPI.getRates().then(r=>{const rt=(r.data?.data||r.data||[]).find(x=>x.from_currency==='USD'&&x.to_currency==='KHR');if(rt)setKhrRate(parseFloat(rt.rate)||4100);}).catch(()=>{});
  },[isOwner]);

  const load=useCallback(async()=>{
    setLd(true);
    try{
      const p={limit:500};
      // Non-owners: always filter to their branch (server also enforces this)
      if(!isOwner&&myBranchId) p.branch_id=myBranchId;
      else if(branchF) p.branch_id=branchF;
      if(statusF!=='All')p.status=statusF;
      if(typeF!=='All')p.type=typeF;
      const r=await paymentAPI.getAll(p);
      setData(r.data.data||[]);
    }catch{toast.error('Failed to load payments')}
    finally{setLd(false)};
  },[branchF,statusF,typeF,isOwner,myBranchId]);

  useEffect(()=>{load()},[load]);

  const filtered=useMemo(()=>{
    let d=data;
    if(search){const q=search.toLowerCase();d=d.filter(p=>(p.payment_number||'').toLowerCase().includes(q)||(p.customer?.name||'').toLowerCase().includes(q)||(p.reference||'').toLowerCase().includes(q));}
    if(methodF!=='All')d=d.filter(p=>p.method?.name===methodF);
    if(dateFrom)d=d.filter(p=>p.payment_date>=dateFrom);
    if(dateTo)d=d.filter(p=>p.payment_date<=dateTo);
    return d;
  },[data,search,methodF,dateFrom,dateTo]);

  const allMethods=useMemo(()=>['All',...Array.from(new Set(data.map(p=>p.method?.name).filter(Boolean)))]  ,[data]);

  const stats=useMemo(()=>{
    const completed=filtered.filter(p=>p.status==='completed'&&p.type==='payment');
    const refunds=filtered.filter(p=>p.type==='refund');
    const pending=filtered.filter(p=>p.status==='pending');
    return{
      net:filtered.reduce((s,p)=>s+(p.type==='refund'?-1:1)*(p.status==='completed'?1:0)*parseFloat(p.amount||0),0),
      khrCollected:filtered.filter(p=>p.original_currency==='KHR'&&p.type==='payment'&&p.status==='completed').reduce((s,p)=>s+parseFloat(p.original_amount||0),0),
      totalPending:pending.reduce((s,p)=>s+parseFloat(p.amount||0),0),
      totalRefunded:refunds.reduce((s,p)=>s+parseFloat(p.amount||0),0),
      countCompleted:completed.length,countPending:pending.length,
      countRefunds:refunds.length,countFailed:filtered.filter(p=>p.status==='failed').length,
    };
  },[filtered]);

  const branchGroups=useMemo(()=>{
    const map={};
    filtered.forEach(p=>{
      const bid=p.branch_id||0;
      if(!map[bid])map[bid]={branch:p.branch||{id:bid,name:'Unassigned'},payments:[]};
      map[bid].payments.push(p);
    });
    return Object.values(map).sort((a,b)=>b.payments.length-a.payments.length);
  },[filtered]);

  const methodStats=useMemo(()=>{
    const map={};
    filtered.filter(p=>p.status==='completed'&&p.type==='payment').forEach(p=>{
      const m=p.method?.name||'Other';
      if(!map[m])map[m]={name:m,count:0,total:0};
      map[m].count++;map[m].total+=parseFloat(p.amount||0);
    });
    return Object.values(map).sort((a,b)=>b.total-a.total);
  },[filtered]);

  const exportCSV=()=>{
    const rows=[['#','Payment #','Date','Customer','Branch','Method','Type','Status','Amount','Reference','Notes'].join(',')];
    filtered.forEach((p,i)=>rows.push([i+1,p.payment_number||`PAY-${p.id}`,p.payment_date,`"${p.customer?.name||''}"`,`"${p.branch?.name||''}"`,p.method?.name||'',p.type,p.status,parseFloat(p.amount||0).toFixed(2),`"${p.reference||''}"`,`"${p.notes||''}"`].join(',')));
    const blob=new Blob([rows.join('\n')],{type:'text/csv'});
    const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download=`payments-${new Date().toISOString().split('T')[0]}.csv`;a.click();
  };

  // Only owners can clear the branch filter
  const hasFilters=(isOwner?branchF:'')  ||statusF!=='All'||typeF!=='All'||methodF!=='All'||dateFrom||dateTo||search;
  const clearFilters=()=>{
    if(isOwner)setBranchF('');
    setStatusF('All');setTypeF('All');setMethodF('All');setDateFrom('');setDateTo('');setSearch('');
  };

  const TABS=[{key:'list',label:'Payment List'},{key:'branches',label:'By Branch'},{key:'analytics',label:'Analytics'}];
  // Hide "By Branch" tab for non-owners (only one branch anyway)
  const visibleTabs=isOwner?TABS:TABS.filter(t=>t.key!=='branches');

  const StatusBadge=({status})=>{
    const sm=STATUS_META[status]||STATUS_META.completed;const Icon=sm.icon;
    return<span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,fontWeight:700,padding:'3px 9px',borderRadius:20,background:sm.color+'18',color:sm.color}}><Icon size={10}/>{sm.label}</span>;
  };

  return(
    <div>
      <PageHeader title="Payments" subtitle={
        !isOwner&&myBranch
          ?<span style={{display:'flex',alignItems:'center',gap:6}}>
              {filtered.length} records
              <span style={{display:'inline-flex',alignItems:'center',gap:4,background:'var(--pm)',color:'var(--primary)',padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:700}}>
                <Lock size={10}/>{myBranch.name}
              </span>
            </span>
          :`${filtered.length} records`
      } action={
        <div className="flex gap-2">
          <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={14}/></button>
          <button className="btn btn-ghost btn-sm" onClick={exportCSV} style={{display:'flex',alignItems:'center',gap:6}}><Download size={14}/> Export CSV</button>
        </div>
      }/>

      {/* Branch-locked banner for non-owners */}
      {!isOwner&&myBranch&&(
        <div style={{display:'flex',alignItems:'center',gap:8,padding:'9px 14px',background:'var(--pm)',borderRadius:10,marginBottom:14,fontSize:12}}>
          <Building2 size={14} color="var(--primary)"/>
          <span style={{color:'var(--primary)',fontWeight:600}}>Branch view:</span>
          <span style={{color:'var(--text)',fontWeight:700}}>{myBranch.name}</span>
          <span style={{color:'var(--td)'}}>— Showing payments for your branch only</span>
          <Lock size={11} color="var(--td)" style={{marginLeft:'auto'}}/>
        </div>
      )}

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10,marginBottom:18}}>
        <StatCard label="Net Collected" value={fmt(stats.net)} sub={`${stats.countCompleted} completed · ${fmtKHR(stats.net)}`} color="var(--success)" icon={TrendingUp}/>
        <StatCard label="Pending" value={fmt(stats.totalPending)} sub={`${stats.countPending} awaiting`} color="var(--warning)" icon={Clock} onClick={()=>setStatusF(statusF==='pending'?'All':'pending')} active={statusF==='pending'}/>
        <StatCard label="Refunded" value={fmt(stats.totalRefunded)} sub={`${stats.countRefunds} refunds`} color="var(--accent)" icon={TrendingDown} onClick={()=>setTypeF(typeF==='refund'?'All':'refund')} active={typeF==='refund'}/>
        <StatCard label="Failed" value={stats.countFailed} sub="transactions" color="var(--danger)" icon={XCircle} onClick={()=>setStatusF(statusF==='failed'?'All':'failed')} active={statusF==='failed'}/>
        {stats.khrCollected>0&&<StatCard label="KHR Collected" value={`៛${Math.round(stats.khrCollected).toLocaleString()}`} sub={fmtKHR(stats.khrCollected/khrRate)} color="var(--warning)" icon={DollarSign}/>}
        {isOwner&&<StatCard label="Branches" value={branchGroups.length} sub="with activity" color="var(--primary)" icon={Building2}/>}
        <StatCard label="Methods" value={methodStats.length} sub="payment methods" color="var(--info)" icon={CreditCard}/>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:4,marginBottom:16,borderBottom:'1px solid var(--border)'}}>
        {visibleTabs.map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            style={{padding:'8px 18px',fontSize:13,fontWeight:600,border:'none',
              borderBottom:`2px solid ${tab===t.key?'var(--primary)':'transparent'}`,
              background:'transparent',cursor:'pointer',color:tab===t.key?'var(--primary)':'var(--tm)',marginBottom:-1,transition:'color .15s'}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{background:'var(--s2)',borderRadius:12,padding:'12px 14px',marginBottom:16,display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
        <div style={{position:'relative',flex:1,minWidth:200}}>
          <Search size={13} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--td)',pointerEvents:'none'}}/>
          <input className="form-input" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search payment #, customer, reference..." style={{paddingLeft:30,fontSize:12}}/>
        </div>

        {/* Branch selector — owners only; non-owners see locked badge */}
        {isOwner?(
          <select className="form-select" value={branchF} onChange={e=>setBranchF(e.target.value)} style={{minWidth:150,fontSize:12}}>
            <option value="">All Branches</option>
            {branches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        ):(
          <div style={{display:'flex',alignItems:'center',gap:5,padding:'7px 10px',borderRadius:8,
            background:'var(--pm)',border:'1.5px solid var(--primary)',fontSize:12,fontWeight:600,color:'var(--primary)'}}>
            <Lock size={11}/>{myBranch?.name||'Your Branch'}
          </div>
        )}

        <select className="form-select" value={statusF} onChange={e=>setStatusF(e.target.value)} style={{minWidth:130,fontSize:12}}>
          {['All','completed','pending','failed','refunded'].map(s=><option key={s} value={s}>{s==='All'?'All Status':s[0].toUpperCase()+s.slice(1)}</option>)}
        </select>
        <select className="form-select" value={typeF} onChange={e=>setTypeF(e.target.value)} style={{minWidth:120,fontSize:12}}>
          {['All','payment','refund'].map(s=><option key={s} value={s}>{s==='All'?'All Types':s[0].toUpperCase()+s.slice(1)}</option>)}
        </select>
        <select className="form-select" value={methodF} onChange={e=>setMethodF(e.target.value)} style={{minWidth:130,fontSize:12}}>
          {allMethods.map(m=><option key={m} value={m}>{m==='All'?'All Methods':m}</option>)}
        </select>
        <input className="form-input" type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{width:135,fontSize:12}} title="From date"/>
        <input className="form-input" type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{width:135,fontSize:12}} title="To date"/>
        {hasFilters&&<button className="btn btn-ghost btn-sm" onClick={clearFilters} style={{color:'var(--danger)',display:'flex',alignItems:'center',gap:4,flexShrink:0}}><X size={12}/> Clear</button>}
      </div>

      {/* LIST TAB */}
      {tab==='list'&&(
        <DataTable loading={ld} columns={[
          {key:'num',label:'Payment #',render:r=><span style={{fontFamily:'monospace',fontWeight:800,fontSize:12,color:'var(--accent)'}}>{r.payment_number||`PAY-${r.id}`}</span>},
          {key:'date',label:'Date',render:r=><div><div style={{fontWeight:600,fontSize:12}}>{fmtDate(r.payment_date)}</div>{r.invoice_id&&<div style={{fontSize:10,color:'var(--primary)',fontFamily:'monospace'}}>Inv #{r.invoice_id}</div>}</div>},
          {key:'customer',label:'Customer',render:r=><span style={{fontWeight:600,fontSize:13}}>{r.customer?.name||'—'}</span>},
          {key:'branch',label:'Branch',render:r=>(
            <div style={{display:'flex',alignItems:'center',gap:6}}>
              <div style={{width:6,height:6,borderRadius:'50%',background:'var(--primary)',flexShrink:0}}/>
              <span style={{fontSize:12,fontWeight:600}}>{r.branch?.name||'—'}</span>
            </div>
          )},
          {key:'method',label:'Method',render:r=>(
            <div style={{display:'flex',alignItems:'center',gap:6}}><CreditCard size={12} color="var(--td)"/><span style={{fontSize:12}}>{r.method?.name||'—'}</span></div>
          )},
          {key:'type',label:'Type',render:r=>{const tm=TYPE_META[r.type]||TYPE_META.payment;return<Badge variant={tm.variant}>{tm.label}</Badge>;}},
          {key:'status',label:'Status',render:r=><StatusBadge status={r.status}/>},
          {key:'amount',label:'Amount',render:r=><div style={{textAlign:'right'}}><div style={{fontWeight:800,fontSize:14,color:r.type==='refund'?'var(--danger)':'var(--success)'}}>{r.type==='refund'?'−':''}{fmt(r.amount)}{r.original_currency==='KHR'&&r.original_amount?<span style={{fontSize:10,fontWeight:400,display:'block',color:'var(--td)'}}>{'≈ '}{'៛'}{r.original_amount!=null?Math.round(r.original_amount).toLocaleString():''} KHR</span>:null}</div><div style={{fontSize:10,color:'var(--td)'}}>{fmtKHR(r.amount)}</div></div>},
          {key:'reference',label:'Reference',render:r=>r.reference?<span style={{fontSize:11,color:'var(--td)',fontFamily:'monospace'}}>{r.reference}</span>:'—'},
          {key:'notes',label:'Notes',render:r=>r.notes?<span style={{fontSize:11,color:'var(--td)',maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',display:'inline-block',verticalAlign:'middle'}}>{r.notes}</span>:'—'},
        ]} data={filtered} actions={r=>(<button className="btn btn-ghost btn-sm" onClick={()=>setDetail(r)} title="View"><Eye size={14}/></button>)}/>
      )}

      {/* BRANCHES TAB — owners only */}
      {tab==='branches'&&isOwner&&(
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:12,marginBottom:20}}>
            {branchGroups.map(g=>(
              <BranchCard key={g.branch?.id||0} branch={g.branch} payments={g.payments}
                onFilter={bid=>setBranchF(String(bid)===branchF?'':String(bid))}
                active={String(g.branch?.id)===branchF}/>
            ))}
            {branchGroups.length===0&&<div style={{gridColumn:'1/-1',textAlign:'center',padding:40,color:'var(--td)'}}>No payments found</div>}
          </div>
          {branchGroups.map(g=>{
            if(branchF&&String(g.branch?.id)!==branchF)return null;
            const collected=g.payments.filter(p=>p.status==='completed'&&p.type==='payment').reduce((s,p)=>s+parseFloat(p.amount||0),0);
            return(
              <div key={g.branch?.id||0} style={{marginBottom:24}}>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,paddingBottom:8,borderBottom:'2px solid var(--primary)'}}>
                  <Building2 size={16} color="var(--primary)"/>
                  <span style={{fontWeight:800,fontSize:14,color:'var(--primary)'}}>{g.branch?.name||'Unassigned'}</span>
                  <span style={{fontSize:12,color:'var(--td)'}}>— {g.payments.length} payments</span>
                  <span style={{marginLeft:'auto',fontWeight:800,color:'var(--success)',fontSize:15}}>{fmt(collected)} collected</span>
                </div>
                <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
                  {Object.entries(STATUS_META).map(([s,sm])=>{
                    const items=g.payments.filter(p=>p.status===s);
                    if(!items.length)return null;
                    const total=items.reduce((a,p)=>a+parseFloat(p.amount||0),0);
                    const Icon=sm.icon;
                    return(
                      <div key={s} style={{display:'flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:8,background:sm.color+'14',border:`1px solid ${sm.color}44`}}>
                        <Icon size={12} color={sm.color}/>
                        <span style={{fontSize:11,fontWeight:700,color:sm.color}}>{sm.label}</span>
                        <span style={{fontSize:11,color:'var(--td)'}}>({items.length})</span>
                        <span style={{fontSize:12,fontWeight:800,color:sm.color}}>{fmt(total)}</span>
                      </div>
                    );
                  })}
                </div>
                <DataTable columns={[
                  {key:'num',label:'Payment #',render:r=><span style={{fontFamily:'monospace',fontSize:11,fontWeight:700,color:'var(--accent)'}}>{r.payment_number||`PAY-${r.id}`}</span>},
                  {key:'date',label:'Date',render:r=>fmtDate(r.payment_date)},
                  {key:'customer',label:'Customer',render:r=><span style={{fontWeight:600}}>{r.customer?.name||'—'}</span>},
                  {key:'method',label:'Method',render:r=>r.method?.name||'—'},
                  {key:'type',label:'Type',render:r=><Badge variant={TYPE_META[r.type]?.variant||'success'}>{r.type}</Badge>},
                  {key:'status',label:'Status',render:r=><StatusBadge status={r.status}/>},
                  {key:'amount',label:'Amount',render:r=><span style={{fontWeight:800,color:r.type==='refund'?'var(--danger)':'var(--success)'}}>{r.type==='refund'?'−':''}{fmt(r.amount)}</span>},
                  {key:'ref',label:'Reference',render:r=>r.reference||'—'},
                  {key:'inv',label:'Invoice',render:r=>r.invoice_id?<span style={{fontSize:11,color:'var(--primary)',fontFamily:'monospace'}}>#{r.invoice_id}</span>:'—'},
                ]} data={g.payments} actions={r=>(<button className="btn btn-ghost btn-sm" onClick={()=>setDetail(r)}><Eye size={13}/></button>)}/>
              </div>
            );
          })}
        </div>
      )}

      {/* ANALYTICS TAB */}
      {tab==='analytics'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:18}}>
            <div style={{fontSize:13,fontWeight:800,marginBottom:14,display:'flex',alignItems:'center',gap:8}}><CreditCard size={15} color="var(--primary)"/> Payment Methods</div>
            {methodStats.length===0?<div style={{textAlign:'center',padding:20,color:'var(--td)'}}>No completed payments</div>:
            methodStats.map((m,i)=>{
              const colors=['var(--primary)','var(--success)','var(--accent)','var(--warning)','var(--info)'];
              const c=colors[i%colors.length];
              const pct=methodStats[0]?.total>0?(m.total/methodStats[0].total*100):0;
              return(
                <div key={m.name} style={{marginBottom:12}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                    <span style={{fontWeight:600,fontSize:12}}>{m.name}</span>
                    <div style={{textAlign:'right'}}><span style={{fontWeight:800,fontSize:13,color:c}}>{fmt(m.total)}</span><span style={{fontSize:10,color:'var(--td)',marginLeft:6}}>{m.count} payments</span></div>
                  </div>
                  <div style={{height:8,background:'var(--s2)',borderRadius:4,overflow:'hidden'}}>
                    <div style={{width:`${pct}%`,height:'100%',background:c,borderRadius:4,transition:'width .4s'}}/>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:18}}>
            <div style={{fontSize:13,fontWeight:800,marginBottom:14,display:'flex',alignItems:'center',gap:8}}><CheckCircle size={15} color="var(--success)"/> Status Breakdown</div>
            {Object.entries(STATUS_META).map(([s,sm])=>{
              const items=filtered.filter(p=>p.status===s);
              if(!items.length)return null;
              const total=items.reduce((a,p)=>a+parseFloat(p.amount||0),0);
              const Icon=sm.icon;
              return(
                <div key={s} style={{display:'flex',alignItems:'center',gap:12,marginBottom:10,padding:'10px 12px',borderRadius:10,background:sm.color+'0e',border:`1px solid ${sm.color}28`}}>
                  <Icon size={16} color={sm.color}/>
                  <div style={{flex:1}}><div style={{fontWeight:700,fontSize:12,color:sm.color}}>{sm.label}</div><div style={{fontSize:11,color:'var(--td)'}}>{items.length} transaction{items.length!==1?'s':''}</div></div>
                  <div style={{fontWeight:800,fontSize:14,color:sm.color}}>{fmt(total)}</div>
                </div>
              );
            })}
          </div>

          {/* Branch ranking — owners only */}
          {isOwner&&(
            <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:18,gridColumn:'1/-1'}}>
              <div style={{fontSize:13,fontWeight:800,marginBottom:14,display:'flex',alignItems:'center',gap:8}}><Building2 size={15} color="var(--primary)"/> Collection by Branch</div>
              {(()=>{
                const sorted=[...branchGroups].map(g=>({
                  branch:g.branch,
                  collected:g.payments.filter(p=>p.status==='completed'&&p.type==='payment').reduce((s,p)=>s+parseFloat(p.amount||0),0),
                  pending:g.payments.filter(p=>p.status==='pending').reduce((s,p)=>s+parseFloat(p.amount||0),0),
                  refunded:g.payments.filter(p=>p.type==='refund').reduce((s,p)=>s+parseFloat(p.amount||0),0),
                  count:g.payments.length,
                })).sort((a,b)=>b.collected-a.collected);
                const maxC=Math.max(...sorted.map(s=>s.collected),1);
                return sorted.map((s,i)=>(
                  <div key={s.branch?.id||i} style={{marginBottom:12}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <span style={{fontSize:11,fontWeight:900,color:'var(--td)',minWidth:18}}>#{i+1}</span>
                        <span style={{fontWeight:700,fontSize:13}}>{s.branch?.name||'Unassigned'}</span>
                      </div>
                      <div style={{display:'flex',gap:12,alignItems:'center'}}>
                        {s.pending>0&&<span style={{fontSize:11,color:'var(--warning)',fontWeight:600}}>{fmt(s.pending)} pending</span>}
                        {s.refunded>0&&<span style={{fontSize:11,color:'var(--accent)',fontWeight:600}}>{fmt(s.refunded)} refunded</span>}
                        <span style={{fontWeight:900,fontSize:14,color:'var(--success)'}}>{fmt(s.collected)}</span>
                        <span style={{fontSize:11,color:'var(--td)'}}>{s.count} txns</span>
                      </div>
                    </div>
                    <div style={{height:10,background:'var(--s2)',borderRadius:5,overflow:'hidden',display:'flex',gap:1}}>
                      <div style={{width:`${s.collected/maxC*100}%`,height:'100%',background:'var(--success)',borderRadius:5,transition:'width .4s'}}/>
                      {s.pending>0&&<div style={{width:`${s.pending/maxC*100}%`,height:'100%',background:'var(--warning)',borderRadius:5}}/>}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}

          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:18,gridColumn:'1/-1'}}>
            <div style={{fontSize:13,fontWeight:800,marginBottom:14}}>Top Customers by Payment Volume</div>
            {(()=>{
              const map={};
              filtered.filter(p=>p.status==='completed'&&p.type==='payment').forEach(p=>{
                const n=p.customer?.name||'Unknown';
                if(!map[n])map[n]={name:n,count:0,total:0};
                map[n].count++;map[n].total+=parseFloat(p.amount||0);
              });
              const top=Object.values(map).sort((a,b)=>b.total-a.total).slice(0,10);
              if(!top.length)return<div style={{textAlign:'center',padding:20,color:'var(--td)'}}>No data</div>;
              const maxT=Math.max(...top.map(t=>t.total),1);
              return(
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12}}>
                  {top.map((c,i)=>(
                    <div key={c.name} style={{display:'flex',alignItems:'center',gap:10}}>
                      <span style={{width:20,fontSize:11,fontWeight:900,color:'var(--td)',textAlign:'right',flexShrink:0}}>#{i+1}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                          <span style={{fontWeight:600,fontSize:12,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}</span>
                          <span style={{fontWeight:800,fontSize:13,color:'var(--success)',marginLeft:8,flexShrink:0}}>{fmt(c.total)}</span>
                        </div>
                        <div style={{height:5,background:'var(--s2)',borderRadius:3,overflow:'hidden'}}>
                          <div style={{width:`${c.total/maxT*100}%`,height:'100%',background:'var(--primary)',borderRadius:3}}/>
                        </div>
                        <div style={{fontSize:10,color:'var(--td)',marginTop:2}}>{c.count} payment{c.count!==1?'s':''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {detail&&<PaymentDetailModal payment={detail} onClose={()=>setDetail(null)}/>}
    </div>
  );
}
